import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { VenetianMask, Clock, CheckCircle2, RotateCcw } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";

export default function SocialInviteTracker({ companionId, compact }) {
  const { t, lang } = useLocale();
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);

  const load = useCallback(() => {
    api.get("/social-invites/mine")
      .then((r) => setInvites(r.data.invites || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    window.addEventListener("si-updated", load);
    return () => window.removeEventListener("si-updated", load);
  }, [load]);

  const shown = companionId ? invites.filter((i) => i.companion_id === companionId) : invites;
  if (!shown.length) return null;

  const statusChip = (status) => {
    if (status === "sent")
      return (
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-green-500/15 text-green-400 border-green-500/40">
          <CheckCircle2 className="w-3 h-3" /> {t("si.trackSent")}
        </span>
      );
    if (status === "declined")
      return (
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-red-500/15 text-red-400 border-red-500/40">
          <RotateCcw className="w-3 h-3" /> {t("si.trackRefunded")}
        </span>
      );
    return (
      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-amber-500/15 text-amber-400 border-amber-500/40">
        <Clock className="w-3 h-3" /> {t("si.trackPending")}
      </span>
    );
  };

  const rows = (
    <div className="space-y-2" data-testid="social-invite-tracker">
      {shown.slice(0, compact ? 3 : 20).map((inv) => (
        <div
          key={inv.request_id}
          className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/5 border border-white/10"
          data-testid={`si-status-${inv.request_id}`}
        >
          <div className="min-w-0">
            <p className="text-sm truncate">
              {inv.invite_type === "secret_admirer" ? "💌" : "💘"}{" "}
              {t("si.trackLine", {
                kind: inv.invite_type === "secret_admirer" ? t("si.trackKindAdmirer") : t("si.trackKindDate"),
                handle: inv.handle,
                platform: inv.platform,
              })}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {t("si.trackVia", { name: inv.companion_name })} · {new Date(inv.created_at).toLocaleDateString(lang)}
            </p>
            {inv.invitee_user_id && (
              <button
                onClick={() => navigate(`/profile/${inv.invitee_user_id}`)}
                className="mt-1.5 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-emerald-500/15 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/25 transition-colors"
                data-testid={`si-joined-${inv.request_id}`}
              >
                🎉 {t("si.trackJoined", { name: inv.invitee_name })}
              </button>
            )}
          </div>
          {statusChip(inv.status)}
        </div>
      ))}
    </div>
  );

  if (compact) return <div className="mt-3">{rows}</div>;

  return (
    <div className="mt-8 glass-card p-6 rounded-2xl" data-testid="social-invite-tracker-card">
      <div className="flex items-center gap-3 mb-4">
        <VenetianMask className="w-5 h-5 text-violet-400" />
        <h2 className="text-lg font-semibold">{t("si.trackTitle")}</h2>
      </div>
      {rows}
    </div>
  );
}
